import { Script, graphql } from "gatsby"
import React from "react"
import { Layout } from "@bw/layouts"
import { Seo, Section, Container, CmsImage } from "@bw/bits"
import { Teasers, PageHeader } from "@bw/modules"
import { Teaser, Breadcrumbs, SidebarLayout } from "@bw/partials"
import { Footer, Content, Header } from "../components/modules/mapModules.jsx"
import { useTranslation } from "react-i18next"
import styled from "styled-components"
import shuffle from "../utils/shuffle"
import dateFormat from "../utils/dateFormat"

const DateWrapper = styled.div`
  padding: 15px 0;
`

export const Head = ({ data }) => (
  <>
    {data.collectionArticles.head && (
      <Script
        id={`user-script-${data.collectionArticles.id}`}
        dangerouslySetInnerHTML={{ __html: data.collectionArticles.head }}
      />
    )}
  </>
)

const ArticleTemplate = ({ data, pageContext, location }) => {
  const { collectionArticles, mount, categories, articles } = data
  const page = collectionArticles
  const related = articles?.nodes ? shuffle(articles.nodes).splice(0, 3) : null
  const { t } = useTranslation()

  const breadcrumbsLinks = [
    {
      title: "Accueil",
      to: "/",
    },
    {
      title: mount.title,
      to: mount.slug,
    },
  ]
  if (page.category) {
    breadcrumbsLinks.push({
      title: page.category.title,
      to: mount.slug + "/" + page.category.slug,
    })
  }

  breadcrumbsLinks.push({
    title: page.title,
    to: page.slug,
  })

  const mountImage = mount.header[0]?.image

  return (
    <Layout
      color={page.color?.value}
      {...{ pageContext, head: page.head, body: page.body }}
    >
      <Seo
        url={location.href}
        title={page.metaTitle || page.title}
        description={page.metaDescription}
        image={page.image?.url}
        meta={[
          {
            name: "robots",
            content: page.doNotIndex
              ? "noindex, nofollow"
              : "max-image-preview:large, index, follow",
          },
        ]}
      />
      <Header
        modules={[
          {
            __typename: "PageHeaderModule",
            title: page.title,
            subtitle: "Restez informés grâce à nos différentes publications.",
            image: mountImage,
          },
        ]}
        page={page}
      />
      {breadcrumbsLinks && <Breadcrumbs breadcrumbs={breadcrumbsLinks} />}
      <SidebarLayout
        sidemenu={categories.nodes.map(c => ({
          title: c.title,
          slug: mount.slug + "/" + c.slug,
        }))}
      >
        <Container>
          {page.date && <DateWrapper>{page.date}</DateWrapper>}
        </Container>
        <Content modules={page.content} />
        {related && related.length > 0 && (
          <Section margin="100px 0" title={t("sameCategory")}>
            <Teasers>
              {related.map((item, i) => {
                return (
                  <Teaser
                    smaller
                    key={i}
                    title={item.title}
                    excerpt={item.teaserExcerpt}
                    to={item.slug}
                    image={
                      <CmsImage
                        src={item.teaserImage || "/placeholder-teaser.jpg"}
                        alt={item.title}
                      />
                    }
                  />
                )
              })}
            </Teasers>
          </Section>
        )}
      </SidebarLayout>
      <Footer modules={page.footer} />
    </Layout>
  )
}

export default ArticleTemplate

export const articleQuery = graphql`
  query articleQuery($id: String!, $category: String) {
    collectionArticles(id: { eq: $id }) {
      id
      head
      body
      title
      metaTitle
      metaDescription
      slug
      date(formatString: "D MMMM YYYY", locale: "fr")
      locale
      color {
        value
      }
      pictogram {
        value
      }
      image {
        url
      }
      category {
        slug
        title
      }
      doNotIndex
      content {
        __typename
        suptitle
        title
        subtitle
        buttonType {
          value
        }
        buttonUrl
        buttonIsExternal
        buttonText
        buttonPosition {
          value
        }
        buttonEntry {
          url
        }
        ancre

        ... on CallToActionModule {
          ...CallToActionModuleFragment
        }
        ... on ContentModule {
          ...ContentModuleFragment
        }
        ... on ContentWithImageModule {
          ...ContentWithImageModuleFragment
        }
        ... on GalleryModule {
          ...GalleryModuleFragment
        }
        ... on ContentOnColumnsModule {
          ...ContentOnColumnsModuleFragment
        }
        ... on TeasersLatestModule {
          ...TeasersLatestModuleFragment
        }
        ... on MinusModule {
          ...MinusModuleFragment
        }
        ... on LogoGridModule {
          ...LogoGridModuleFragment
        }
        ... on ImageModule {
          ...ImageModuleFragment
        }
        ... on VideoModule {
          ...VideoModuleFragment
        }
        ... on DownloadsModule {
          ...DownloadsModuleFragment
        }
      }
      footer {
        __typename
        suptitle
        title
        subtitle
        buttonType {
          value
        }
        buttonUrl
        buttonIsExternal
        buttonText
        buttonPosition {
          value
        }
        buttonEntry {
          url
        }
      }
    }
    articles: allCollectionArticles(
      filter: { category: { slug: { eq: $category } }, id: { ne: $id } }
    ) {
      nodes {
        category {
          title
          slug
        }
        id
        title
        slug
        teaserExcerpt
        teaserImage {
          localImage {
            childImageSharp {
              gatsbyImageData(width: 351, height: 139)
            }
          }
        }
      }
    }
    categories: allTaxonomyCategory(
      filter: { entries_count: { gt: 0 } }
      sort: { title: ASC }
    ) {
      nodes {
        title
        slug
      }
    }
    mount: collectionPages(mount: { handle: { eq: "articles" } }) {
      slug
      title
      header {
        ... on PageHeaderModule {
          image {
            url
          }
        }
      }
    }
  }
`
